import { useState, useEffect, useCallback } from 'react';
import { Download, Plus, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import OrdersTable from '../components/orders/OrdersTable';
import OrderDetailsModal from '../components/common/OrderDetailsModal';
import CreateOrderModal from '../components/orders/CreateOrderModal';
import ConfirmDeleteModal from '../components/common/ConfirmDeleteModal';
import ToastContainer from '../components/common/Toast';
import { useDebounce } from '../hooks/useDebounce';
import { useToast } from '../hooks/useToast';
import * as ordersApi from '../services/ordersApi';
import { getApiErrorMessage } from '../utils/apiErrors';

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState({
    page: 0,
    size: 20,
    totalElements: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [sortBy, setSortBy] = useState('date-desc');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Toast notifications
  const { toasts, removeToast, success, error } = useToast();

  // Debounce search query to reduce API calls
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // Reset to page 0 when search query changes
  useEffect(() => {
    if (pagination.page !== 0) {
      setPagination(prev => ({ ...prev, page: 0 }));
    }
  }, [debouncedSearchQuery]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      // Parse sortBy to get field and direction
      const [sortField, sortDirection] = sortBy.split('-');
      const sortParam = sortField === 'date'
        ? `createdAt,${sortDirection}`
        : `totalAmount,${sortDirection}`;

      const params = {
        page: pagination.page,
        size: pagination.size,
        sort: sortParam,
      };

      // Add status filter if not 'all'
      if (statusFilter !== 'all') {
        params.status = statusFilter.toUpperCase();
      }

      // Add search parameter if present
      if (debouncedSearchQuery) {
        params.search = debouncedSearchQuery;
      }

      const response = await ordersApi.getOrders(params);
      const data = response.content || [];

      // Transform API data to match OrdersTable expected format
      const transformedData = data.map(order => ({
        ...order,
        orderNumber: order.uniqueId || order.orderNumber,
        customer: order.fullName || order.customer,
        customerEmail: order.email || order.customerEmail,
        date: order.createdAt || order.date,
        total: order.totalAmount || order.total,
        status: order.status ? order.status.toLowerCase() : 'pending',
      }));

      setOrders(transformedData);

      // Update pagination info from response - only update if values changed
      // toPageInt: accepts numbers and numeric strings, rejects objects/null/NaN
      const toPageInt = (v) => {
        if (v === null || v === undefined || typeof v === 'object') return null;
        const n = Math.floor(Number(v));
        return Number.isFinite(n) ? n : null;
      };
      setPagination(prev => {
        const newPagination = {
          page: toPageInt(response.number) ?? toPageInt(response.page) ?? prev.page,
          size: toPageInt(response.size) ?? prev.size,
          totalElements: toPageInt(response.totalElements) ?? data.length,
          totalPages: toPageInt(response.totalPages) ?? 1,
        };

        // Only update if values actually changed to prevent unnecessary re-renders
        if (
          prev.page === newPagination.page &&
          prev.size === newPagination.size &&
          prev.totalElements === newPagination.totalElements &&
          prev.totalPages === newPagination.totalPages
        ) {
          return prev;
        }

        return newPagination;
      });

      // Show toast notification if statuses were auto-refreshed from shipping providers
      if (response.statusRefreshInfo && response.statusRefreshInfo.totalRefreshed > 0) {
        const { yalidineRefreshed, zrExpressRefreshed, totalRefreshed } = response.statusRefreshInfo;
        const details = [];
        if (yalidineRefreshed > 0) {
          details.push(`${yalidineRefreshed} via Yalidine`);
        }
        if (zrExpressRefreshed > 0) {
          details.push(`${zrExpressRefreshed} via ZR Express`);
        }
        const message = `${totalRefreshed} commande${totalRefreshed > 1 ? 's ont été mises' : ' a été mise'} à jour depuis les transporteurs (${details.join(', ')})`;
        success(message);
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
      setOrders([]);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [pagination.page, pagination.size, debouncedSearchQuery, statusFilter, sortBy, success]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleSearchChange = (query) => {
    setSearchQuery(query);
  };

  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
    if (pagination.page !== 0) {
      setPagination(prev => ({ ...prev, page: 0 }));
    }
  };

  const handleStatusFilterChange = (newStatus) => {
    setStatusFilter(newStatus);
    if (pagination.page !== 0) {
      setPagination(prev => ({ ...prev, page: 0 }));
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newSize) => {
    setPagination(prev => ({ ...prev, size: newSize, page: 0 }));
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setIsModalOpen(true);
  };

  const handleUpdateStatus = async (orderId, status) => {
    try {
      // Fetch the complete order data from the API to get all fields including orderItems
      const fullOrder = await ordersApi.getOrderById(orderId);

      // Send all fields as-is from the API response, only updating the status
      const updatedOrder = await ordersApi.updateOrder(orderId, {
        ...fullOrder,
        status: status.toUpperCase(),
      });

      fetchOrders();

      // Build success message based on status and tracking info
      if (status.toLowerCase() === 'confirmed' || status.toLowerCase() === 'shipped') {
        const trackingInfo = updatedOrder.trackingNumber || updatedOrder.providerOrderId;

        if (trackingInfo) {
          success(`Statut mis à jour avec succès. Numéro de suivi: ${trackingInfo}`);
        } else if (!updatedOrder.shippingLabelUrl) {
          // Parcel creation failed - no shipping label URL
          error('La création du colis chez le transporteur a échoué', 'Attention');
        } else {
          success('Le statut de la commande a été mis à jour avec succès');
        }
      } else {
        success('Le statut de la commande a été mis à jour avec succès');
      }

      return updatedOrder;
    } catch (err) {
      console.error('Error updating order status:', err);
      error(getApiErrorMessage(err), 'Erreur lors de la mise à jour');
      throw err;
    }
  };

  const handleSaveOrder = async (orderId, formData) => {
    try {
      const fullOrder = await ordersApi.getOrderById(orderId);

      const updatedOrder = await ordersApi.updateOrder(orderId, {
        ...fullOrder,
        fullName: formData.fullName,
        phone: formData.phone,
        email: formData.email,
        wilaya: formData.wilaya,
        city: formData.city,
        streetAddress: formData.streetAddress,
        shippingMethod: formData.shippingMethod,
        shippingProvider: formData.shippingProvider,
        shippingCost: formData.shippingCost,
        stopDeskId: formData.stopDeskId,
        status: formData.status.toUpperCase(),
      });

      // Refresh orders list
      fetchOrders();

      // Update selectedOrder so the modal reflects the saved state
      const transformed = {
        ...updatedOrder,
        orderNumber: updatedOrder.uniqueId || updatedOrder.orderNumber,
        customer: updatedOrder.fullName || updatedOrder.customer,
        customerEmail: updatedOrder.email || updatedOrder.customerEmail,
        date: updatedOrder.createdAt || updatedOrder.date,
        total: updatedOrder.totalAmount || updatedOrder.total,
        status: updatedOrder.status ? updatedOrder.status.toLowerCase() : 'pending',
      };
      setSelectedOrder(transformed);

      // Handle tracking info for status changes
      const newStatus = formData.status.toLowerCase();
      if (newStatus === 'confirmed' || newStatus === 'shipped') {
        const trackingInfo = updatedOrder.trackingNumber || updatedOrder.providerOrderId;
        if (trackingInfo) {
          success(`Commande mise à jour. Numéro de suivi: ${trackingInfo}`);
        } else if (!updatedOrder.shippingLabelUrl) {
          error('La création du colis chez le transporteur a échoué', 'Attention');
        } else {
          success('La commande a été mise à jour avec succès');
        }
      } else {
        success('La commande a été mise à jour avec succès');
      }

      return updatedOrder;
    } catch (err) {
      console.error('Error saving order:', err);
      error(getApiErrorMessage(err), 'Erreur lors de la sauvegarde');
      throw err;
    }
  };

  const handleCreateOrder = async (orderData) => {
    try {
      await ordersApi.createOrder(orderData);
      setIsCreateModalOpen(false);
      fetchOrders();
      success('La commande a été créée avec succès');
    } catch (err) {
      console.error('Error creating order:', err);
      error(getApiErrorMessage(err), 'Erreur lors de la création');
    }
  };

  const handleDelete = (order) => {
    setOrderToDelete(order);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!orderToDelete) return;
    setIsDeleting(true);
    try {
      await ordersApi.deleteOrder(orderToDelete.id);
      setIsDeleteModalOpen(false);
      setOrderToDelete(null);
      fetchOrders();
      success('La commande a été supprimée avec succès');
    } catch (err) {
      console.error('Error deleting order:', err);
      error(getApiErrorMessage(err), 'Erreur lors de la suppression');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setOrderToDelete(null);
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await ordersApi.exportOrders();

      // Create a download link for the blob
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'orders_export.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      success('Les commandes ont été exportées avec succès');
    } catch (err) {
      console.error('Error exporting orders:', err);
      error(getApiErrorMessage(err), "Erreur lors de l'export");
    } finally {
      setIsExporting(false);
    }
  };

  if (initialLoad && loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Commandes</h1>
          <p className="text-gray-600 mt-1">Gérez les commandes clients et suivez les livraisons</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-sm whitespace-nowrap text-sm sm:text-base"
          >
            <Plus className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
            <span>Ajouter une commande</span>
          </motion.button>

          <motion.button
            whileHover={{ scale: isExporting ? 1 : 1.02 }}
            whileTap={{ scale: isExporting ? 1 : 0.98 }}
            onClick={handleExport}
            disabled={isExporting}
            className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 shadow-sm whitespace-nowrap text-sm sm:text-base disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isExporting
              ? <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 animate-spin" />
              : <Download className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />}
            <span>{isExporting ? 'Export...' : 'Exporter'}</span>
          </motion.button>
        </div>
      </div>

      <OrdersTable
        orders={orders}
        onViewOrder={handleViewOrder}
        onDelete={handleDelete}
        onUpdateStatus={handleUpdateStatus}
        sortBy={sortBy}
        onSortChange={handleSortChange}
        statusFilter={statusFilter}
        onStatusFilterChange={handleStatusFilterChange}
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        loading={loading}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onRefresh={fetchOrders}
      />

      <OrderDetailsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        order={selectedOrder}
        onUpdateStatus={handleUpdateStatus}
        onSaveOrder={handleSaveOrder}
      />

      <CreateOrderModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateOrder}
      />

      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        itemName={orderToDelete ? `la commande ${orderToDelete.orderNumber}` : 'cet élément'}
        isLoading={isDeleting}
      />

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
};

export default Orders;
